import React, { useState, useEffect, useRef, useCallback } from "react";
import sortBy from "lodash/sortBy";
import isEqual from "lodash/isEqual";
import { Button, Dropdown, Checkbox, Divider } from "antd";

import { DownOutlined } from "@ant-design/icons";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { CheckboxChangeEvent } from "antd/lib/checkbox";

import CustomMenu from "./CustomMenu";
import { OptionGroup } from "./styledComponents";

type DropdownOption = {
  label: string;
  value: CheckboxValueType;
};

/**
 * An extra entry rendered between "Select all" and the option list, for a
 * selection the option list cannot express on its own.
 *
 * Two shapes, told apart by whether `values` is set:
 *  - **shortcut** (`values`): ticking it selects exactly those values and
 *    unticking clears them again; it renders ticked only while the selection is
 *    exactly those values. Other options stay selectable alongside it — doing so
 *    simply drops the tick, because the selection is then no longer that shortcut.
 *  - **standalone** (no `values`): a filter that lives outside the option list.
 *    Mark it `exclusive` when the backend ANDs the two conditions and they
 *    therefore cannot both be active: while it is on, the option list is cleared
 *    and rendered muted, and ticking any option turns it off again. The options
 *    stay clickable on purpose — clicking one is the way back out of it.
 *
 * Purely additive — with the prop absent the menu renders exactly as before.
 */
export type QuickFilterOption = {
  /** Identifies the entry in the `onChange` payload. */
  key: string;
  label: string;
  /** Whether the entry is active in the *applied* filter state. */
  checked: boolean;
  values?: CheckboxValueType[];
  exclusive?: boolean;
};

type Props = {
  values: CheckboxValueType[];
  options: DropdownOption[];
  disabled?: boolean;
  withFilter?: boolean;
  moveSelectedToTheTop?: boolean;
  name: string;
  placeholder?: string;
  onChange: (
    newValues: CheckboxValueType[],
    quickFilters?: Record<string, boolean>
  ) => void;
  withCheckAll?: boolean;
  quickFilters?: QuickFilterOption[];
};

const dropdownWidth = 160;

const buttonStyle = {
  width: `${dropdownWidth + 10}px`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

/**
 * The applied state of the standalone entries only. A shortcut entry is always
 * derived from the option selection, so it has no state of its own to track.
 */
const standaloneState = (quickFilters: QuickFilterOption[]) =>
  Object.fromEntries(
    quickFilters
      .filter(({ values }) => !values)
      .map(({ key, checked }) => [key, checked])
  );

export default function DropdownMultiSelect({
  name,
  placeholder,
  values,
  options,
  disabled = false,
  withFilter = true,
  moveSelectedToTheTop = false,
  onChange,
  withCheckAll = false,
  quickFilters = [],
}: Props) {
  const [selectedValues, setSelectedValues] =
    useState<CheckboxValueType[]>(values);
  const [checkAll, setCheckAll] = useState(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [haveChangedValues, setHaveChangedValues] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("");
  const [selectedStandalone, setSelectedStandalone] = useState<
    Record<string, boolean>
  >(() => standaloneState(quickFilters));

  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);

  const selectedCount = values.length;

  // Follow the applied state when it changes from outside this dropdown (for
  // example the "only internal transfers" checkbox resetting the filters).
  // Serialized so the effect keys on the values, not on a fresh array identity.
  const appliedStandalone = JSON.stringify(standaloneState(quickFilters));
  useEffect(() => {
    setSelectedStandalone(JSON.parse(appliedStandalone));
  }, [appliedStandalone]);

  // The applied option selection changes from outside too — the "only internal
  // transfers" checkbox rewrites the categories twice on its way in and out.
  // `selectedValues` seeds from `values` once on mount, so without this it keeps
  // options the parent has already dropped and hands them back on the next
  // apply. Keyed on the serialized values, so a re-render that changes nothing
  // leaves a pending edit in an open dropdown alone.
  const appliedValues = JSON.stringify(values);
  useEffect(() => {
    const applied: CheckboxValueType[] = JSON.parse(appliedValues);
    setSelectedValues(applied);
    if (withCheckAll) {
      setCheckAll(applied.length > 0 && applied.length === options.length);
    }
  }, [appliedValues, withCheckAll, options.length]);

  const getOptions = useCallback(
    () =>
      moveSelectedToTheTop
        ? sortBy(options, (opt) => !values.includes(opt.value))
        : options,
    [options, values, moveSelectedToTheTop]
  );

  const valuesForCheckAll = getOptions().map((option) => option.value);

  const hasExclusiveSelected = quickFilters.some(
    ({ key, exclusive, values: shortcut }) =>
      exclusive && !shortcut && selectedStandalone[key]
  );

  const clearExclusive = useCallback(
    (current: Record<string, boolean>) => {
      const cleared = { ...current };
      quickFilters.forEach(({ key, exclusive, values: shortcut }) => {
        if (exclusive && !shortcut) {
          cleared[key] = false;
        }
      });
      return cleared;
    },
    [quickFilters]
  );

  const onValueChange = useCallback(
    (checkedValue: CheckboxValueType[]) => {
      setSelectedValues(checkedValue);
      setHaveChangedValues(true);
      // Selecting an option is the way out of an exclusive quick filter.
      setSelectedStandalone(clearExclusive);
      if (withCheckAll) {
        setCheckAll(checkedValue.length === valuesForCheckAll.length);
      }
    },
    [setSelectedValues, withCheckAll, valuesForCheckAll, clearExclusive]
  );

  const isQuickFilterChecked = useCallback(
    ({ key, values: shortcut }: QuickFilterOption) =>
      shortcut
        ? // Exactly those values, so that "Select all" — which necessarily
          // covers them — does not read as the shortcut being active.
          shortcut.length === selectedValues.length &&
          shortcut.every((value) => selectedValues.includes(value))
        : !!selectedStandalone[key],
    [selectedValues, selectedStandalone]
  );

  const applyChanges = useCallback(
    () =>
      onChange(
        selectedValues,
        Object.fromEntries(
          quickFilters.map((quickFilter) => [
            quickFilter.key,
            isQuickFilterChecked(quickFilter),
          ])
        )
      ),
    [selectedValues, quickFilters, isQuickFilterChecked, onChange]
  );

  const onCheckAllChange = (event: CheckboxChangeEvent) => {
    setSelectedValues(event.target.checked ? valuesForCheckAll : []);
    setCheckAll(event.target.checked);
    setHaveChangedValues(true);
    setSelectedStandalone(clearExclusive);
  };

  const onQuickFilterChange = (
    { key, values: shortcut, exclusive }: QuickFilterOption,
    checked: boolean
  ) => {
    setHaveChangedValues(true);

    if (shortcut) {
      // Ticking it *is* the selection, so that its ticked state and the
      // selection can never disagree; unticking takes those values back out.
      const newValues = checked
        ? [...shortcut]
        : selectedValues.filter((value) => !shortcut.includes(value));
      setSelectedValues(newValues);
      setSelectedStandalone(clearExclusive);
      if (withCheckAll) {
        setCheckAll(newValues.length === valuesForCheckAll.length);
      }
      return;
    }

    setSelectedStandalone((current) => ({
      ...(exclusive && checked ? clearExclusive(current) : current),
      [key]: checked,
    }));

    if (exclusive && checked) {
      // The two conditions are ANDed by the backend, so an exclusive quick
      // filter and an option selection cannot both be active.
      setSelectedValues([]);
      setCheckAll(false);
    }
  };

  const onDropdownToggle = useCallback(
    (show: boolean) => {
      if (!show && haveChangedValues) {
        applyChanges();
      }
      setShowDropdown(show);
      withFilter && setFilter("");
      setHaveChangedValues(false);
    },
    [applyChanges, haveChangedValues, withFilter]
  );

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        onDropdownToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, toggleRef, onDropdownToggle]);

  // A standalone quick filter selects no options, so without this the toggle
  // would read "All categories" and hide the fact that a filter is active.
  const appliedStandaloneLabels = quickFilters
    .filter(({ checked, values: shortcut }) => checked && !shortcut)
    .map(({ label }) => label);

  const dropdownText = selectedCount
    ? `${selectedCount} selected`
    : appliedStandaloneLabels.length
    ? appliedStandaloneLabels.join(", ")
    : placeholder || `Select a ${name}`;

  const hasEntriesAboveDivider = withCheckAll || quickFilters.length > 0;

  const menu = (
    <div ref={menuRef}>
      <CustomMenu
        filter={filter}
        setFilter={withFilter ? setFilter : undefined}
        dropdownDisabled={disabled}
        disableApply={
          isEqual(values, selectedValues) &&
          isEqual(standaloneState(quickFilters), selectedStandalone)
        }
        onReset={() => {
          setSelectedValues([]);
          setHaveChangedValues(true);
          withFilter && setFilter("");
          setCheckAll(false);
          setSelectedStandalone((current) =>
            Object.fromEntries(Object.keys(current).map((key) => [key, false]))
          );
        }}
        onApply={() => onDropdownToggle(false)}
        id={`${name}-dropdown-custom-menu`}
      >
        {hasEntriesAboveDivider && (
          <>
            {withCheckAll && (
              <Checkbox onChange={onCheckAllChange} checked={checkAll}>
                Select all
              </Checkbox>
            )}
            {quickFilters.map((quickFilter) => (
              <div key={quickFilter.key}>
                <Checkbox
                  checked={isQuickFilterChecked(quickFilter)}
                  disabled={disabled}
                  onChange={(event: CheckboxChangeEvent) =>
                    onQuickFilterChange(quickFilter, event.target.checked)
                  }
                >
                  {quickFilter.label}
                </Checkbox>
              </div>
            ))}
            <Divider style={{ margin: "12px 0" }} />
          </>
        )}
        <OptionGroup
          $muted={hasExclusiveSelected}
          options={getOptions()}
          onChange={(checkedValue: CheckboxValueType[]) => {
            onValueChange(checkedValue);
          }}
          disabled={disabled}
          value={selectedValues}
          style={{ display: "flex", flexDirection: "column" }}
        />
      </CustomMenu>
    </div>
  );

  return (
    <Dropdown overlay={menu} trigger={["click"]} visible={showDropdown}>
      <Button
        ref={toggleRef}
        onClick={() => setShowDropdown(!showDropdown)}
        style={buttonStyle}
      >
        {dropdownText} <DownOutlined />
      </Button>
    </Dropdown>
  );
}
